import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { industriesData } from '../../data/industriesData';
import { servicesData } from '../../data/servicesData';
import { useModal } from '../../contexts/ModalContext';
const industrySeoData = {
  'healthcare': {
    metaTitle: "Healthcare Software & Clinic Management Solutions | Byte Pairs",
    metaDescription: "Secure patient record management, automated doctor booking schedules, and WhatsApp reminders to reduce no-shows. Build custom clinic software in Dharapuram.",
    keywords: "clinic management software Dharapuram, doctor scheduling app, electronic health records India",
    faq: [
      {
        q: "How does online booking reduce clinic no-shows?",
        a: "Patients book slots on a live calendar, and our automated WhatsApp alerts confirm appointments and remind patients before their visit, reducing no-shows by up to 50%."
      }
    ]
  },
  'retail': {
    metaTitle: "POS Billing & Inventory Management Software for Retail | Byte Pairs",
    metaDescription: "Optimized POS billing software with barcode support and real-time inventory tracking. Sync physical shop stocks with custom online e-commerce stores.",
    keywords: "POS billing software Dharapuram, retail inventory software, e-commerce web developer Dharapuram",
    faq: [
      {
        q: "Can we connect physical retail stock with our online store?",
        a: "Yes, we design inventory sync systems that sync stock levels in real time across physical storefronts and custom e-commerce channels."
      }
    ]
  },
  'education': {
    metaTitle: "School Management Systems & Student Portals | Byte Pairs",
    metaDescription: "Lightweight student databases, digital attendance sheet logs, fee collection portals, and teacher-parent notice boards without software bloat.",
    keywords: "school management system India, student portal developer, fee payment gateway integration",
    faq: [
      {
        q: "What functions are covered by your school software?",
        a: "We build portals for timetables, reports, fee collections, and automated attendance sheets where teachers can take rolls in under three clicks."
      }
    ]
  },
  'restaurant': {
    metaTitle: "QR Code Menus & Restaurant Management Software | Byte Pairs",
    metaDescription: "Digital QR ordering menus, kitchen order displays, and maps search optimization. Maximize foot traffic and speed table orders for restaurants.",
    keywords: "QR menu ordering system, restaurant management software, local maps search ranking Dharapuram",
    faq: [
      {
        q: "What are the benefits of QR code ordering?",
        a: "Customers scan QR codes at tables to view digital menus and place orders. This reduces order communication errors and saves menu printing costs."
      }
    ]
  },
  'hotel': {
    metaTitle: "Direct Hotel Booking Engines & Guest Management | Byte Pairs",
    metaDescription: "Avoid OTA commissions. Build custom booking engines directly on your hotel site, with room service QR portals and automated WhatsApp check-in confirmations.",
    keywords: "hotel booking engine development, guest room service portal, direct room booking software",
    faq: [
      {
        q: "Why should we build a direct booking engine?",
        a: "A custom booking engine integrates into your site, letting guests pay deposits directly and saving you from paying high commission percentages to travel websites."
      }
    ]
  },
  'real-estate': {
    metaTitle: "Real Estate Websites & Customer CRM Systems | Byte Pairs",
    metaDescription: "Fast-loading property directories, interactive location filters, and custom CRM layouts for real estate agents. Capture and score buyer leads automatically.",
    keywords: "real estate listing site, property search filter database, agent CRM platform",
    faq: [
      {
        q: "How does the lead tracking system help real estate agents?",
        a: "All lead preferences, budget, and search locations are automatically logged in a central CRM dashboard, alerting agents when matching properties load."
      }
    ]
  },
  'construction': {
    metaTitle: "Builder Portfolio Websites & Field Logs for Contractors | Byte Pairs",
    metaDescription: "High-resolution project galleries, local builder maps ranking setups, and simple mobile field report sheets for construction supervisors.",
    keywords: "construction portfolio web designer, builder local SEO, contractor daily site log app",
    faq: [
      {
        q: "How does digital construction logging work?",
        a: "We build simple, mobile-friendly forms for site foremen to log daily materials and progress updates, syncing instantly with the main office database."
      }
    ]
  },
  'manufacturing': {
    metaTitle: "Manufacturing Databases & Factory Batch Tracking Software | Byte Pairs",
    metaDescription: "Replace paper logging sheets. Create custom batch dashboards, raw stock alerts, and automated preventive machine maintenance calendars.",
    keywords: "factory batch tracker, manufacturing inventory database, equipment maintenance calendar app",
    faq: [
      {
        q: "How does automated machine maintenance tracking help?",
        a: "The database tracks machine uptime hours and auto-reminds factory managers of service dates, avoiding unexpected machine downtime and production pauses."
      }
    ]
  },
  'travel': {
    metaTitle: "Travel Package Bookings & Itinerary Planners | Byte Pairs",
    metaDescription: "Interactive itinerary builders, secure online deposit forms, and group inquiry handlers. Build dynamic travel agency platforms optimized for mobile.",
    keywords: "travel agency booking system, tour package itinerary planner, travel booking deposits",
    faq: [
      {
        q: "Can we take booking deposits online through the itinerary page?",
        a: "Yes, we integrate local payment gateways so travelers can pay booking deposits or full package fees directly from their mobile screen."
      }
    ]
  },
  'professional-services': {
    metaTitle: "Professional Consultant Websites & Booking Calendars | Byte Pairs",
    metaDescription: "Establish authority online. Build clean websites for lawyers, accountants, and consultants with calendar bookings (Calendly) and secure client intake forms.",
    keywords: "consulting website builder, Calendly integration developer, client intake form portal",
    faq: [
      {
        q: "How do calendar integrations reduce friction?",
        a: "Instead of back-and-forth emails, clients choose open meeting slots from your schedule, pay if needed, and receive automatic Zoom or Google Meet links."
      }
    ]
  }
};

const IndustryDetailPage = () => {
  const { industryId } = useParams();
  const navigate = useNavigate();
  const { openConsultation } = useModal();
  const industry = industriesData[industryId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [industryId]);

  if (!industry) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center px-6">
        <Icon name="AlertTriangle" size={48} className="text-warning mb-4" />
        <h1 className="text-2xl font-bold mb-2">Industry Sector Not Found</h1>
        <p className="text-text-secondary mb-6 text-center max-w-md">
          The industry you are looking for is not listed. Check out our homepage for sectors we serve.
        </p>
        <Button variant="default" onClick={() => navigate('/')}>
          Return Home
        </Button>
      </div>
    );
  }

  const seo = industrySeoData[industryId] || {
    metaTitle: `${industry.title} Digital Solutions | Byte Pairs`,
    metaDescription: `${industry.tagline} Discover how Byte Pairs solves core challenges in the ${industry.title.toLowerCase()} sector.`,
    keywords: `bytepairs, byte pairs, software for ${industry.title.toLowerCase()}, local digital transformation`,
    faq: []
  };

  const industrySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seo.metaTitle,
    "description": seo.metaDescription,
    "url": `https://bytepairs.com/industries/${industry.id}`,
    "about": {
      "@type": "Thing",
      "name": industry.title,
      "description": industry.overview
    },
    "publisher": {
      "@type": "ProfessionalService",
      "name": "Byte Pairs",
      "telephone": "+916374241548",
      "email": "bytepairs2026@gmail.com",
      "url": "https://bytepairs.com/",
      "image": "https://bytepairs.com/android-chrome-512x512.png",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Dharapuram",
        "addressLocality": "Dharapuram",
        "addressRegion": "Tamil Nadu",
        "postalCode": "638656",
        "addressCountry": "IN"
      }
    }
  };

  const faqSchema = seo.faq && seo.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": seo.faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://bytepairs.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Industries",
        "item": "https://bytepairs.com/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": industry.title,
        "item": `https://bytepairs.com/industries/${industry.id}`
      }
    ]
  };

  // Find detailed info for related services
  const matchedServices = industry.relatedServices
    .map(srvId => servicesData[srvId])
    .filter(Boolean);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <>
      <Helmet>
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href={`https://bytepairs.com/industries/${industry.id}`} />
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://bytepairs.com/industries/${industry.id}`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.metaTitle} />
        <meta name="twitter:description" content={seo.metaDescription} />
        <script type="application/ld+json">
          {JSON.stringify(industrySchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative py-20 bg-gradient-to-br from-primary/5 via-muted/30 to-success/5 overflow-hidden border-b border-border">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.1)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            </div>
            <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Icon name={industry.icon} size={16} />
                  <span>Sectors We Serve</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-4xl md:text-6xl font-black mb-6 tracking-tight"
              >
                {industry.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-10"
              >
                {industry.tagline}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand font-bold"
                  onClick={openConsultation}
                >
                  Request Industry consultation
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="border-primary/20 text-primary hover:bg-primary/5"
                  onClick={() => {
                    const el = document.getElementById('challenges-section');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View Sector Challenges
                </Button>
              </motion.div>
            </div>
          </section>

          {/* Overview Statement */}
          <section className="py-20 bg-background">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl font-bold mb-6">How Tech Empowers {industry.title}</h2>
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed">
                {industry.overview}
              </p>
            </div>
          </section>

          {/* Challenges vs. Transformations split view */}
          <section id="challenges-section" className="py-20 bg-muted/20 border-t border-b border-border">
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                {/* Left Side: Traditional Obstacles */}
                <div>
                  <h3 className="text-2xl font-black mb-8 flex items-center space-x-3 text-destructive">
                    <Icon name="AlertTriangle" size={24} />
                    <span>Common Inefficiencies</span>
                  </h3>
                  <div className="space-y-8">
                    {industry.challenges.map((challenge, idx) => (
                      <div key={idx} className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0 text-destructive text-sm font-bold">
                          {idx + 1}
                        </div>
                        <p className="text-text-secondary leading-relaxed font-medium pt-0.5">{challenge}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Side: Digital Transformations */}
                <div>
                  <h3 className="text-2xl font-black mb-8 flex items-center space-x-3 text-success">
                    <Icon name="Zap" size={24} />
                    <span>How We Transform Them</span>
                  </h3>
                  <div className="space-y-8">
                    {industry.digitalTransformations.map((trans, idx) => {
                      const [title, desc] = trans.split(': ');
                      return (
                        <div key={idx} className="flex items-start space-x-4">
                          <Icon name="CheckCircle" size={22} className="text-success flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-foreground block mb-1">{title}</span>
                            <span className="text-text-secondary text-sm leading-relaxed">{desc}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-20 bg-background">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-black">Expected Operational Improvements</h2>
                <p className="text-text-secondary mt-4 max-w-2xl mx-auto">
                  Adding direct, lightweight software solves core tasks and saves ongoing resources.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {industry.benefits.map((benefit, idx) => (
                  <motion.div
                    key={idx}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px' }}
                    variants={itemVariants}
                    className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg transition-all duration-300 text-center"
                  >
                    <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                      <Icon name="Check" className="text-success" size={24} />
                    </div>
                    <p className="text-foreground leading-relaxed font-semibold">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Related Services */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-success/5 border-t border-b border-border">
            <div className="max-w-6xl mx-auto px-6 text-center">
              <h2 className="text-3xl font-black mb-4">Recommended Services for {industry.title}</h2>
              <p className="text-text-secondary max-w-2xl mx-auto mb-12">
                We frequently combine these service blocks to build tailored solutions for this sector.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {matchedServices.map((srv) => (
                  <Link
                    key={srv.id}
                    to={`/services/${srv.id}`}
                    className="bg-card border border-border rounded-2xl p-6 hover:border-primary/50 hover:shadow-brand transition-all duration-300 text-left flex flex-col justify-between"
                  >
                    <div>
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon name={srv.icon} className="text-primary" size={20} />
                      </div>
                      <h4 className="font-bold text-foreground mb-2">{srv.title}</h4>
                      <p className="text-text-secondary text-xs line-clamp-3 leading-relaxed">
                        {srv.overview}
                      </p>
                    </div>
                    <div className="mt-4 text-xs font-semibold text-primary flex items-center space-x-1">
                      <span>Learn more</span>
                      <Icon name="ChevronRight" size={12} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Simple CTA */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-success/5 text-center">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-3xl md:text-5xl font-black mb-6">Ready to Optimize Your Workflows?</h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-8">
                Let's discuss how customized, direct software tools can improve your {industry.title.toLowerCase()} operations. Free consultations, transparent planning.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  className="bg-gradient-to-r from-primary to-accent text-white"
                  onClick={openConsultation}
                >
                  Schedule Free Business Discussion
                </Button>
                <Link to="/contact-engagement-hub" className="text-sm font-semibold text-text-secondary hover:text-primary transition-colors">
                  Contact details & timing
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default IndustryDetailPage;
